import React from 'react';

interface Player {
  id: string;
  name: string;
}

interface Pairing {
  player1Id: string;
  player1Name: string;
  player2Id: string;
  player2Name: string;
  totalMatchesTogether: number;
  timesOnSameTeam: number;
  percentage: number;
}

interface PlayerPairingMatrixProps {
  players: Player[];
  pairings: Pairing[];
}

export const PlayerPairingMatrix: React.FC<PlayerPairingMatrixProps> = ({ players, pairings }) => {
  // Helper function to get pairing data for a specific pair
  const getPairing = (player1Id: string, player2Id: string): Pairing | undefined => {
    return pairings.find(
      (p) =>
        (p.player1Id === player1Id && p.player2Id === player2Id) ||
        (p.player1Id === player2Id && p.player2Id === player1Id)
    );
  };

  // Helper function to get cell color based on percentage
  const getCellColor = (percentage: number): string => {
    if (percentage < 25 || percentage > 75) {
      // Red for highly imbalanced (< 25% or > 75%)
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
    } else if (percentage >= 35 && percentage <= 65) {
      // Green for balanced (35-65%)
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
    } else {
      // Orange for moderate imbalance (25-34% or 66-75%)
      return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200';
    }
  };

  if (players.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No player data available
      </div>
    );
  }

  return (
    <>
      <div className="w-full border border-gray-200 dark:border-gray-700 rounded-lg overflow-auto max-h-[1000px]">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
                Player
              </th>
              {players.map((player) => (
                <th
                  key={player.id}
                  className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[80px] bg-gray-50 dark:bg-gray-800"
                >
                  <div className="truncate" title={player.name}>
                    {player.name.split(' ').map((n) => n[0]).join('')}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {players.map((player1, rowIndex) => (
              <tr key={player1.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="sticky left-0 z-10 px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700">
                  {player1.name}
                </td>
                {players.map((player2, colIndex) => {
                  // Diagonal cells (same player)
                  if (player1.id === player2.id) {
                    return (
                      <td
                        key={player2.id}
                        className="px-2 py-2 text-center text-sm bg-gray-100 dark:bg-gray-800"
                      >
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      </td>
                    );
                  }

                  // Get pairing data (works for both above and below diagonal)
                  const pairing = getPairing(player1.id, player2.id);

                  if (!pairing || pairing.totalMatchesTogether === 0) {
                    return (
                      <td
                        key={player2.id}
                        className="px-2 py-2 text-center text-sm text-gray-400 dark:text-gray-500"
                      >
                        <div className="text-xs">—</div>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={player2.id}
                      className={`px-2 py-2 text-center text-sm font-medium ${getCellColor(pairing.percentage)}`}
                      title={`${pairing.player1Name} & ${pairing.player2Name}: ${pairing.timesOnSameTeam}/${pairing.totalMatchesTogether} matches together (${pairing.percentage}%)`}
                    >
                      <div className="text-sm font-bold">{pairing.percentage}%</div>
                      <div className="text-xs opacity-75">
                        {pairing.timesOnSameTeam}/{pairing.totalMatchesTogether}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="w-full">
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-12 h-8 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded flex items-center justify-center text-green-800 dark:text-green-200 font-semibold text-xs">
              50%
            </div>
            <span className="text-gray-700 dark:text-gray-300">Balanced (35-65%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-8 bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded flex items-center justify-center text-orange-800 dark:text-orange-200 font-semibold text-xs">
              30%
            </div>
            <span className="text-gray-700 dark:text-gray-300">Moderate (25-34% or 66-75%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-8 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded flex items-center justify-center text-red-800 dark:text-red-200 font-semibold text-xs">
              20%
            </div>
            <span className="text-gray-700 dark:text-gray-300">Highly Imbalanced (&lt;25% or &gt;75%)</span>
          </div>
        </div>

        <div className="mt-4 text-xs text-gray-600 dark:text-gray-400">
          <p>
            <strong>Note:</strong> Percentages show how often two players have been on the same team
            in their last 10 matches together. Numbers below show (matches on same team / total matches together).
          </p>
        </div>
      </div>
    </>
  );
};
